/**
 * Manual flight control — whether the calibration rings are painted at rest.
 *
 * The rings (speed presets, the envelope, the operator's cap, the dead zone)
 * are how a pixel offset reads as a speed: they are the graduations on the
 * control. But they are drawn over an operational map, and an operator who is
 * WATCHING rather than flying is looking through them at the picture.
 *
 * So this is a display choice and nothing else. `ringGeometry` computes the
 * same geometry either way, `setpointFromPointer` clamps against the same
 * radii, and the keyboard path scales to the same `outerRadiusPx` — hiding the
 * paint cannot change what the aircraft is told. See `ManualMapControl` for the
 * one thing this deliberately does NOT hide: the yaw band is the only pointer
 * path to yaw, and an invisible control is not a tidier control.
 *
 * Everything here is a plain value or a pure function — no React, no DOM — so
 * the rules can be tested without rendering a COP, as `manual-layout.ts` is.
 */

/**
 * When the graduations are painted.
 *
 * `active` is not "never": the rings come back for the duration of a drag, a
 * yaw or a held flight key. Clutter is a complaint about the RESTING map, and
 * an operator mid-gesture is owed the speed scale they are aiming at — flying
 * to a numeric readout alone is a downgrade nobody asked for.
 */
export type ManualRingVisibility = 'always' | 'active';

/** Ordered as the toggle reports them. */
export const MANUAL_RING_VISIBILITIES: readonly ManualRingVisibility[] = ['always', 'active'];

/** Today's behaviour. A new operator sees the graduations without hunting for a setting. */
export const DEFAULT_MANUAL_RING_VISIBILITY: ManualRingVisibility = 'always';

export const MANUAL_RING_LABEL: Record<ManualRingVisibility, string> = {
  always: 'RINGS',
  active: 'RINGS',
};

/** Announced in words: `aria-pressed` alone leaves a forced-colors operator guessing. */
export const MANUAL_RING_DESCRIPTION: Record<ManualRingVisibility, string> = {
  always: 'Speed rings always shown',
  active: 'Speed rings shown only while commanding',
};

const STORAGE_PREFIX = 'manual_flight_rings';

/**
 * Keyed by operator, for the reason the layout key is: a shared console must
 * not let one operator's display choice arrive under another's login.
 */
export function manualRingStorageKey(operator?: string | null): string {
  const who = operator?.trim();
  return who ? `${STORAGE_PREFIX}:${who}` : STORAGE_PREFIX;
}

export function isManualRingVisibility(value: unknown): value is ManualRingVisibility {
  return typeof value === 'string' && (MANUAL_RING_VISIBILITIES as readonly string[]).includes(value);
}

/**
 * The operator's stored choice, or the default.
 *
 * Never throws. `localStorage` can throw outright (privacy mode, enterprise
 * policy) and a stored value can be anything at all if another build wrote it.
 * This is a preference about paint: failing to read one must never stop an
 * operator taking manual control.
 */
export function readManualRingVisibility(operator?: string | null): ManualRingVisibility {
  if (typeof window === 'undefined') return DEFAULT_MANUAL_RING_VISIBILITY;
  try {
    const stored = window.localStorage.getItem(manualRingStorageKey(operator));
    return isManualRingVisibility(stored) ? stored : DEFAULT_MANUAL_RING_VISIBILITY;
  } catch {
    return DEFAULT_MANUAL_RING_VISIBILITY;
  }
}

/** Returns false when the choice could not be persisted, so a caller can say so. */
export function writeManualRingVisibility(
  visibility: ManualRingVisibility,
  operator?: string | null,
): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(manualRingStorageKey(operator), visibility);
    return true;
  } catch {
    return false;
  }
}

/**
 * Are the graduations painted right now?
 *
 * The single rule both the surface and its tests read, so "hidden" cannot come
 * to mean two different things in two places.
 */
export function ringsPainted(visibility: ManualRingVisibility, inputActive: boolean): boolean {
  return visibility === 'always' || inputActive;
}
